import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useContactActions } from 'contacts/hooks';
import { IContact, IContactGroup } from 'contacts/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useAgGridTableContext, useNotificationContext } from 'core/states';
import { corporateService } from 'corporate/services';

import { changeContactGroupingSchema } from './schemas';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

interface IChangeContactGroupingForm {
  contactGrouping: IContactGroup;
}

export const ChangeContactGrouping = ({ isOpen, onClose, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();
  const { clearNotification } = useNotificationContext();

  const { selectedRows, deselectAllRows } = useAgGridTableContext();
  const { updateContactGroups } = useContactActions();

  const handleOnClose = (reset?: UseFormReset<IChangeContactGroupingForm>) => {
    clearNotification();
    reset?.();
    onClose();
  };

  const onSubmit =
    (reset: UseFormReset<IChangeContactGroupingForm>) =>
    async (data: IChangeContactGroupingForm) => {
      const contacts = selectedRows.map((contact: IContact) => ({
        id: contact.id,
        contactGroupingId: data.contactGrouping.id,
      }));

      const { success } = await updateContactGroups(contacts);

      if (success) {
        setTimeout(() => {
          handleOnClose(reset);
          deselectAllRows();
          onSubmitSuccess?.();
        }, 1000);
      }
    };

  return (
    <FormProvider schema={changeContactGroupingSchema()} defaultValues={{ contactGrouping: null }}>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={t('titleChangeContactGrouping')}
        submitBtnLabel={t('buttonLabelChangeContactGrouping')}
        onSubmit={onSubmit}
      >
        <FormField
          name="contactGrouping"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelContactGrouping'),
            placeholder: t('placeholderSelectContactGrouping'),
            getOptions: corporateService.getContactGroupings,
            getLabel: (option: IContactGroup) => option.name,
            required: true,
            enabledOnFocus: false,
          }}
        />
      </Form>
    </FormProvider>
  );
};
